export interface Category {
  name: string;
  slug: string;
  url: string;
}

export function getCategoryIcon(slug: string): string {
  const iconMap: Record<string, string> = {
    'shoes': 'footprint-line',
    'hoodies-sweaters': 't-shirt-line',
    't-shirts': 'shirt-line',
    'jackets': 'hanger-line',
    'pants-shorts': 'pant-line',
    'headwear': 'bear-smile-line',
    'sets': 'shapes-line',
    'underwear-underpants': 'user-line',
    'jersey': 'football-line',
    'accessories': 'glasses-line',
    'bags': 'handbag-line',
    'belts': 'links-line',
    'perfumes': 'drop-line',
    'other-stuff': 'more-line',
  };
  return iconMap[slug] || 'shopping-bag-line';
}

export const categories: Category[] = [
  { name: 'Shoes', slug: 'shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Hoodies / Sweaters', slug: 'hoodies-sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { name: 'T-Shirts', slug: 't-shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { name: 'Jackets', slug: 'jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { name: 'Pants / Shorts', slug: 'pants-shorts', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
  { name: 'Headwear', slug: 'headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
  { name: 'Sets', slug: 'sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
  { name: 'Underwear / Underpants', slug: 'underwear-underpants', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
  { name: 'Jersey', slug: 'jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
  { name: 'Accessories', slug: 'accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  { name: 'Bags', slug: 'bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { name: 'Belts', slug: 'belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
  { name: 'Perfumes', slug: 'perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
  { name: 'Other Stuff', slug: 'other-stuff', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
];

export interface ExploreCategory {
  name: string;
  slug: string;
  description: string;
}

export const exploreCategories: ExploreCategory[] = [
  { name: 'Kakobuy Spreadsheet', slug: 'kakobuy-spreadsheet', description: 'Updated product collections, trending finds, and organized shopping lists' },
  { name: 'CSSBuy Spreadsheet', slug: 'cssbuy-spreadsheet', description: 'Curated CSSBuy product discoveries and buying recommendations' },
  { name: 'Superbuy Spreadsheet', slug: 'superbuy-spreadsheet', description: 'Handpicked Superbuy finds with detailed comparisons' },
  { name: 'Oopbuy Spreadsheet', slug: 'oopbuy-spreadsheet', description: 'Top Oopbuy product picks and shopping guides' },
  { name: 'Fashion Spreadsheet', slug: 'fashion-spreadsheet', description: 'Trending fashion items from streetwear to luxury pieces' },
  { name: 'Sneaker Spreadsheet', slug: 'sneaker-spreadsheet', description: 'Latest sneaker finds, releases, and authentic reviews' },
  { name: 'Electronics Spreadsheet', slug: 'electronics-spreadsheet', description: 'Best gadget deals and tech product recommendations' },
  { name: 'Luxury Find Spreadsheet', slug: 'luxury-find-spreadsheet', description: 'Premium product discoveries and high-end shopping guides' },
];

export interface FAQItem {
  question: string;
  answer: string;
}

export const faqItems: FAQItem[] = [
  {
    question: 'What is a Kakobuy Spreadsheet?',
    answer: 'A Kakobuy Spreadsheet is a carefully organized document containing product discoveries, shopping recommendations, price comparisons, and verified product links. Instead of searching through thousands of individual product listings, users can browse a structured spreadsheet to find trending items, popular purchases, and hidden gems across multiple categories including shoes, clothing, accessories, and electronics.',
  },
  {
    question: 'How often is the Kakobuy Spreadsheet updated?',
    answer: 'The Kakobuy Spreadsheet is updated regularly with new product finds, trending items, and community recommendations. New categories and products are added as shopping trends evolve, ensuring you always have access to the latest discoveries. We recommend checking back frequently for fresh additions and seasonal collections.',
  },
  {
    question: 'Is Kakobuy safe to use for shopping?',
    answer: 'Shopping through organized spreadsheets like Kakobuy can be a great way to discover products efficiently. We recommend always verifying product details, checking seller ratings, reviewing shipping policies, and reading customer feedback before making any purchase. The spreadsheet itself is a discovery tool - all transactions happen on verified platforms.',
  },
  {
    question: 'How do I find the best products using the spreadsheet?',
    answer: 'Start by browsing through the category sections that interest you most. Each product entry in the spreadsheet typically includes key information such as price range, quality indicators, popularity metrics, and direct links. Use the category filters to narrow down your search, compare similar items side by side, and check the community ratings to identify top picks.',
  },
  {
    question: 'Can I access the spreadsheet on mobile devices?',
    answer: 'Yes, the Kakobuy Spreadsheet and all related product discovery pages on kakobuyfinds.site are fully optimized for mobile browsing. You can easily explore categories, compare products, and follow links to make purchases directly from your smartphone or tablet with a smooth, responsive experience.',
  },
  {
    question: 'What categories are available in the spreadsheet?',
    answer: 'The Kakobuy Spreadsheet covers a wide range of shopping categories including Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Underwear, Jerseys, Accessories, Bags, Belts, Perfumes, and more. New categories are added based on trending searches and community interest.',
  },
];

export interface Benefit {
  icon: string;
  title: string;
  description: string;
}

export const benefits: Benefit[] = [
  {
    icon: 'ri-time-line',
    title: 'Save Hours of Research',
    description: 'Skip the endless scrolling and manual searching. Our organized spreadsheets compile the best finds so you can discover great products in minutes instead of hours.',
  },
  {
    icon: 'ri-fire-line',
    title: 'Discover Trending Products',
    description: 'Stay ahead of the curve with curated collections featuring the hottest items people are actually buying and talking about right now.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Compare Items Easily',
    description: 'Side-by-side product comparisons help you evaluate quality, pricing, and popularity to make informed shopping decisions quickly.',
  },
  {
    icon: 'ri-compass-discover-line',
    title: 'Find Hidden Product Gems',
    description: 'Uncover unique and underrated products that you would not find through regular browsing. Our community helps surface the best hidden finds.',
  },
];

export interface HowItWorksStep {
  step: number;
  icon: string;
  title: string;
  description: string;
}

export const howItWorksSteps: HowItWorksStep[] = [
  {
    step: 1,
    icon: 'ri-search-eye-line',
    title: 'Browse Updated Product Lists',
    description: 'Explore our regularly updated category spreadsheets featuring the latest product discoveries and trending items across popular shopping categories.',
  },
  {
    step: 2,
    icon: 'ri-bar-chart-grouped-line',
    title: 'Compare Products And Reviews',
    description: 'Review detailed product information, quality indicators, community feedback, and pricing data to find exactly what you are looking for.',
  },
  {
    step: 3,
    icon: 'ri-shopping-bag-3-line',
    title: 'Visit Main Platform And Start Shopping',
    description: 'Once you have found the perfect items, follow the links to complete your purchase on the main shopping platform with confidence.',
  },
];